/**
 * Event dispatcher — fans one {@link AnalyticsEvent} out to every active backend.
 *
 * Seeded with GA (via GTM) + PostHog; both no-op until their transport is on `window`,
 * so wiring up another tracker is just editing this list or calling
 * {@link registerAnalyticsProvider} at startup. Call sites never know which (or how
 * many) backends are active.
 */
import { gtmProvider, posthogProvider } from './providers';
import type { AnalyticsEvent, AnalyticsProvider } from './types';

const providers: AnalyticsProvider[] = [gtmProvider, posthogProvider];

/** Register another backend at runtime. Ignores duplicates (by `name`). */
export function registerAnalyticsProvider(provider: AnalyticsProvider): void {
  if (providers.some((p) => p.name === provider.name)) return;
  providers.push(provider);
}

/** Fan one event out to every active provider. Never throws — analytics must not break UI. */
export function track(event: AnalyticsEvent): void {
  for (const provider of providers) {
    try {
      provider.track(event);
    } catch {
      /* swallow — a broken tracker must never break the configurator */
    }
  }
}
